import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  financialYearLabel,
  financialYearOf,
  financialYearRange,
  profitAndLoss,
  profitAndLossCsv,
  type PeriodGrain,
  type ProfitLoss,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, SelectField } from '../components/Fields.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import { niceTicks } from '../ops/KmPerDayChart.tsx';
import { loadAllInvoices, moneyText, type Invoice } from './api.ts';
import { loadCosts, loadGstRegistered, saveGstRegistered, type Cost } from './costsApi.ts';
import { todayIso } from './CostsPanel.tsx';
import { TableWrap } from '../components/TableWrap.tsx';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const aud = (n: number): string => moneyText(n, 'AUD');
/** Offer text as a download. */
function browserSave(fileName: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Income (invoices issued) against costs per month, quarter or financial year, with GST for the BAS (spec §6). */
export function ProfitLossPanel({ sb }: { readonly sb: SupabaseClient }): ReactElement {
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [costs, setCosts] = useState<Cost[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const thisFy = financialYearOf(todayIso());
  const [grain, setGrain] = useState<PeriodGrain>('month');
  const [fromFy, setFromFy] = useState(String(thisFy));
  const [toFy, setToFy] = useState(String(thisFy));
  // decision 1001: Braynewave is not registered for GST; the setting is stored for every admin (platform_books)
  const [registered, setRegistered] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setError(null);
    Promise.all([loadAllInvoices(sb), loadCosts(sb), loadGstRegistered(sb)])
      .then(([i, c, g]) => {
        setInvoices(i);
        setCosts(c);
        setRegistered(g);
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb]);
  useEffect(reload, [reload]);

  const years = useMemo(() => {
    const dates = [...(invoices ?? []).map((i) => i.issuedOn), ...(costs ?? []).map((c) => c.invoiceDate)];
    const first = Math.min(thisFy, ...dates.map(financialYearOf));
    const out: number[] = [];
    for (let y = thisFy; y >= first; y -= 1) out.push(y);
    return out;
  }, [invoices, costs, thisFy]);

  const pnl = useMemo<ProfitLoss | null>(() => {
    if (!invoices || !costs || registered === null) return null;
    const a = Math.min(Number(fromFy), Number(toFy));
    const b = Math.max(Number(fromFy), Number(toFy));
    return profitAndLoss({
      invoices: invoices.map((i) => ({
        issuedOn: i.issuedOn,
        amount: i.amount,
        gst: i.gst,
        currency: i.currency,
        status: i.status,
      })),
      costs: costs.map((c) => ({ ...c, deleted: c.deletedAt !== null })),
      grain,
      from: financialYearRange(a).from,
      to: financialYearRange(b).to,
      gstRegistered: registered,
    });
  }, [invoices, costs, grain, fromFy, toFy, registered]);

  const yearOptions = years.map((y) => [String(y), financialYearLabel(y)] as const);
  const gst = pnl?.gstRegistered ?? true;

  return (
    <div className="stack">
      {error && <ErrorState message={error} onRetry={reload} />}
      {!pnl && !error && <Loading what="the profit and loss" />}
      {pnl && (
        <div className="panel stack">
          <h2 className="heading">Profit and loss</h2>
          <p className="caption muted">
            Income is the invoices issued to organisations (not void), ex GST, by issue date; costs are the
            costs received in AUD by invoice date ({gst ? 'ex GST' : 'GST included'}). The financial year runs
            1 July to 30 June; quarters are the BAS quarters.
          </p>
          <div className="form-grid">
            <SelectField
              label="Show"
              value={grain}
              onChange={setGrain}
              options={[
                ['month', 'By month'],
                ['quarter', 'By quarter'],
                ['fy', 'By financial year'],
              ]}
            />
            <SelectField label="From" value={fromFy} onChange={setFromFy} options={yearOptions} />
            <SelectField label="To" value={toFy} onChange={setToFy} options={yearOptions} />
          </div>
          <CheckField
            label="Not registered for GST (count GST on costs as a cost)"
            checked={!registered}
            disabled={saving}
            onChange={(notRegistered) => {
              setSaving(true);
              setSaveError(null);
              saveGstRegistered(sb, !notRegistered)
                .then(() => setRegistered(!notRegistered))
                .catch((e: unknown) => setSaveError(message(e)))
                .finally(() => setSaving(false));
            }}
          />
          {saveError && (
            <p className="notice notice--fail" role="alert">
              {saveError}
            </p>
          )}
          {!gst && (
            <p className="notice notice--warn">
              Not registered for GST: GST paid on costs counts as part of each cost, and there are no BAS
              lines (GST collected, paid and net). Untick when Braynewave registers; the change is recorded
              and applies for every admin.
            </p>
          )}
          <p className="body" role="status">
            Income {aud(pnl.total.incomeExGst)}, costs {aud(pnl.total.costsExGst)}, profit{' '}
            {aud(pnl.total.profit)}
            {gst ? `; GST collected ${aud(pnl.total.gstCollected)}, paid ${aud(pnl.total.gstPaid)}.` : '.'}
          </p>
          {pnl.leftOut.length > 0 && (
            <p className="notice notice--warn">
              Not counted:{' '}
              {pnl.leftOut
                .map((l) => `${l.count} ${l.count === 1 ? 'invoice' : 'invoices'} issued in ${l.currency}`)
                .join(', ')}
              .
            </p>
          )}
          <PnlChart pnl={pnl} />
          {/* the chart's figures, periods compared down each column: a table on a phone too (rule 5) */}
          <TableWrap cards={false}>
            <table className="table">
              <thead>
                <tr>
                  <th className="label">Period</th>
                  <th className="label">Income ex GST</th>
                  <th className="label">of which paid</th>
                  <th className="label">not paid yet</th>
                  <th className="label">{gst ? 'Costs ex GST' : 'Costs incl GST'}</th>
                  <th className="label">Profit</th>
                  {gst && (
                    <>
                      <th className="label">GST collected</th>
                      <th className="label">GST paid</th>
                      <th className="label">GST net</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {[...pnl.rows.map((r) => [r.period.label, r] as const), ['Total', pnl.total] as const].map(
                  ([label, r]) => (
                    <tr key={label}>
                      <td>{label === 'Total' ? <strong>Total</strong> : label}</td>
                      <td className="data">{aud(r.incomeExGst)}</td>
                      <td className="data">{aud(r.incomePaidExGst)}</td>
                      <td className="data">{aud(r.incomeUnpaidExGst)}</td>
                      <td className="data">{aud(r.costsExGst)}</td>
                      <td className="data">{r.profit < 0 ? `Loss ${aud(-r.profit)}` : aud(r.profit)}</td>
                      {gst && (
                        <>
                          <td className="data">{aud(r.gstCollected)}</td>
                          <td className="data">{aud(r.gstPaid)}</td>
                          <td className="data">{aud(r.gstNet)}</td>
                        </>
                      )}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </TableWrap>
          <div className="row">
            <Button
              onClick={() =>
                browserSave(`teslyn-profit-and-loss-${grain}-${fromFy}-${toFy}.csv`, profitAndLossCsv(pnl))
              }
            >
              Export CSV
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// SVG units are CSS pixels, as the Operations Hub's km chart
const W = 720;
const PLOT_H = 200;
const TOP = 20;
const LEFT = 72;
const RIGHT = 8;
const AXIS_H = 28;
const H = TOP + PLOT_H + AXIS_H;
const BAR_MAX = 18;
const GAP = 2;
const RADIUS = 4;

function column(x: number, w: number, y: number, h: number): string {
  const r = Math.min(RADIUS, w / 2, h);
  const b = y + h;
  return `M${x},${b}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${b}Z`;
}

/** Income (chart-1) and costs (chart-2) per period, the km chart's marks; profit is in the table below. */
function PnlChart({ pnl }: { readonly pnl: ProfitLoss }): ReactElement {
  const [active, setActive] = useState<number | null>(null);
  const rows = pnl.rows;
  const max = Math.max(0, ...rows.flatMap((r) => [r.incomeExGst, r.costsExGst]));
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1]!;
  const yOf = (v: number): number => TOP + PLOT_H - (Math.max(0, v) / top) * PLOT_H;
  const band = (W - LEFT - RIGHT) / Math.max(1, rows.length);
  const barW = Math.max(1.5, Math.min(BAR_MAX, ((band * 2) / 3 - GAP) / 2));
  const every = band >= 60 ? 1 : band * 3 >= 60 ? 3 : 12;
  const short = (label: string): string => label.replace(/ \(Q\d FY .*\)$/, '');
  const tip = active === null ? null : rows[active];
  return (
    <figure className="chart" aria-label="Income and costs per period">
      <ul className="chart__legend caption">
        <li>
          <span className="chart__swatch chart__swatch--accepted" aria-hidden="true" />
          Income ex GST
        </li>
        <li>
          <span className="chart__swatch chart__swatch--flown" aria-hidden="true" />
          Costs
        </li>
      </ul>
      <div className="chart__frame">
        <svg
          className="chart__svg"
          width="100%"
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          role="group"
          aria-label={`Income ${aud(pnl.total.incomeExGst)}, costs ${aud(pnl.total.costsExGst)}`}
          onPointerLeave={() => setActive(null)}
        >
          <g aria-hidden="true">
            {ticks.map((v) => (
              <g key={v}>
                <line className="chart__grid" x1={LEFT} x2={W - RIGHT} y1={yOf(v)} y2={yOf(v)} />
                <text className="data chart__tick" x={LEFT - 8} y={yOf(v) + 4} textAnchor="end">
                  {v.toLocaleString('en-AU')}
                </text>
              </g>
            ))}
            <text className="data chart__tick" x={4} y={TOP - 8}>
              AUD
            </text>
            <line className="chart__axis" x1={LEFT} x2={W - RIGHT} y1={yOf(0)} y2={yOf(0)} />
          </g>
          {rows.map((r, i) => {
            const x0 = LEFT + i * band;
            const pairX = x0 + (band - (2 * barW + GAP)) / 2;
            const hi = yOf(0) - yOf(r.incomeExGst);
            const hc = yOf(0) - yOf(r.costsExGst);
            return (
              <g
                key={r.period.key}
                className={active === i ? 'chart__day chart__day--on' : 'chart__day'}
                role="img"
                aria-label={`${r.period.label}: income ${aud(r.incomeExGst)}, costs ${aud(r.costsExGst)}, profit ${aud(r.profit)}`}
                tabIndex={0}
                onPointerEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
              >
                <rect className="chart__hit" x={x0} y={TOP} width={band} height={PLOT_H + AXIS_H} />
                {hi > 0 && (
                  <path className="chart__bar--accepted" d={column(pairX, barW, yOf(r.incomeExGst), hi)} />
                )}
                {hc > 0 && (
                  <path
                    className="chart__bar--flown"
                    d={column(pairX + barW + GAP, barW, yOf(r.costsExGst), hc)}
                  />
                )}
                {i % every === 0 && (
                  <text
                    className="data chart__tick"
                    x={x0 + band / 2}
                    textAnchor="middle"
                    y={TOP + PLOT_H + 18}
                    aria-hidden="true"
                  >
                    {short(r.period.label)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        {tip && (
          <div
            className="chart__tooltip"
            role="status"
            style={{ left: '50%', transform: 'translateX(-50%)' }}
          >
            <p className="caption muted">{tip.period.label}</p>
            <p className="caption">
              <span className="chart__key chart__key--accepted" aria-hidden="true" />
              <strong className="data">{aud(tip.incomeExGst)}</strong>
              <span className="muted">Income</span>
            </p>
            <p className="caption">
              <span className="chart__key chart__key--flown" aria-hidden="true" />
              <strong className="data">{aud(tip.costsExGst)}</strong>
              <span className="muted">Costs</span>
            </p>
            <p className="caption muted">
              {tip.profit < 0 ? `Loss ${aud(-tip.profit)}` : `Profit ${aud(tip.profit)}`}
            </p>
          </div>
        )}
      </div>
    </figure>
  );
}
